/** 手机触控：独立操作按钮使用至少44px点击区域；桌面保持原紧凑布局，业务权限、提交与跳转逻辑不变。 */
import React, { useState, useRef, useMemo } from "react";
import { authStore } from "../../lib/api";
import { toast } from "sonner";
import { X, UploadCloud, Download, Eye, Lock, FileText, Image as ImageIcon, Box, GitCompare, CheckSquare, Square, Trash2, Loader2 } from "lucide-react";
import { fmtD } from "../../lib/usDate";
import { MediaLightbox, mediaKind, OFFICE_EXT } from "../../components/MediaLightbox";
import { ProjectPdfPreview } from "../../components/pdf/ProjectPdfPreview";
import { useContextMenu } from "../../components/ContextMenu";

import { T, TE } from "../../lib/i18n";
import { uploadChunked, UPLOAD_MAX } from "../../lib/upload";
import { showContractWarnings } from "../../lib/contractWarn";
import { downloadFile } from "../../lib/fileDownload";
import FileUploadDate, { uploadDateLabel } from "./v3/FileUploadDate";
import { FILE_TYPE_ZH, FILE_TYPE_HINT, DESIGN_UPLOAD_ACCEPT } from "./v3/meta";
import { uploadPbDrawings } from "./v3/pbDrawings";
const BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fileUrl = (fid) => `${BASE}/files/${fid}/download?auth=${authStore.get("ali_token") || ""}`;
const fmtSize = (b) => (!b ? "—" : b > 1048576 ? `${(b / 1048576).toFixed(1)}MB` : `${Math.max(1, Math.round(b / 1024))}KB`);
const iconFor = (ext) => (["dwg", "dxf"].includes(ext) ? Box : ["png", "jpg", "jpeg", "webp", "gif"].includes(ext) ? ImageIcon : FileText);
const STATUS_COLORS = { Draft: "bg-slate-100 text-slate-600", Submitted: "bg-amber-100 text-amber-700", Approved: "bg-emerald-100 text-emerald-700", Superseded: "bg-slate-100 text-slate-400" };
/* 单文件上限（2026-10-22）：从 60MB 直传改成**分片上传**，上限跟全站分片通道一致
   （UPLOAD_MAX = 200MB，见 lib/upload.js）。设计部要传的是整套 CAD / 现场测量原图，
   100-200MB 很常见；直传撑不住，也与设计产物上传（design_v3.v3_disc_upload）口径统一。 */
const MAX_UPLOAD = UPLOAD_MAX;

export default function TaskFilesModal({ task, api, zh, isMgr, canEdit = true, pairedTasks = null, onClose, onChanged }) {
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(null); // 正在删除的版本 aid：行内按钮转圈，避免连点重复发请求
  const ctx = useContextMenu("design-file-ctx");
  const [dragOver, setDragOver] = useState(false);
  const [ftype, setFtype] = useState((task.requiredFileTypes || [])[0] || "Deliverable");
  const [fstatus, setFstatus] = useState("Draft");
  const [selected, setSelected] = useState([]); // aids selected for compare (max 2)
  const [preview, setPreview] = useState(null); // lightbox index
  const [compare, setCompare] = useState(false);
  const inputRef = useRef(null);

  const files = task.files || [];
  const typeOptions = useMemo(() => {
    if (pairedTasks) return ["P+B PDF", "P+B CAD"];
    const s = new Set([...(task.requiredFileTypes || []), "Deliverable", ...files.map((f) => f.type)]);
    return [...s].filter(Boolean);
  }, [task, files, pairedTasks]);

  const doUpload = async (fileList) => {
    const all = Array.from(fileList || []);
    if (!all.length) return;
    // 超限的先剔掉再传，其余正常上传（不让一个大文件拖死整批）
    const arr = all.filter((f) => f.size <= MAX_UPLOAD);
    all.filter((f) => f.size > MAX_UPLOAD).forEach((f) => toast.error(zh
      ? `「${f.name}」${fmtSize(f.size)} 超过 200MB 上限，已跳过`
      : `${f.name} (${fmtSize(f.size)}) exceeds 200MB, skipped`));
    if (!arr.length) { if (inputRef.current) inputRef.current.value = ""; return; }
    setUploading(true);
    try {
      if (pairedTasks) {
        await uploadPbDrawings(api, arr, pairedTasks);
        toast.success(zh ? `已上传 ${arr.length} 份 P+B drawings` : `Uploaded ${arr.length} P+B drawings`);
        onChanged && (await onChanged());
        return;
      }
      for (const f of arr) {
        /* 【2026-10-22】改走**分片上传**（与设计产物上传同一条通道）。
           原来这里是 multipart 直传 `POST /design/tasks/{tid}/upload`，后端 60MB 硬顶，
           整套 CAD 传不上来。现在：先 /uploads/chunk + /uploads/finalize 拿 fileId，
           再带 fileId + 元信息登记成任务文件版本 —— 服务端不经手字节。
           `shrink: false`：保持与原来直传一致，不对图片做压缩改写。 */
        const up = await uploadChunked(api, f, { shrink: false });
        const fd = new FormData();
        fd.append("fileId", up.fileId);
        fd.append("fileName", up.filename || f.name);
        fd.append("fileSize", String(up.size ?? f.size));
        fd.append("contentType", up.contentType || f.type || "application/octet-stream");
        fd.append("type", ftype);
        fd.append("status", fstatus);
        const { data } = await api.post(`/design/tasks/${task.id}/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        // 合同槽位的门牌软校验（只提示不拦截）：文件名门牌 ≠ 项目地址门牌时黄色告警
        showContractWarnings(data?.warnings, zh);
      }
      toast.success(zh ? `已上传 ${arr.length} 个文件（新版本）` : `Uploaded ${arr.length}`);
      onChanged && (await onChanged());
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || (zh ? "上传失败" : "Upload failed"));
      if (pairedTasks) onChanged && (await onChanged());
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const changeStatus = async (f, status) => {
    try {
      const refs = pairedTasks ? f.pairRefs || [] : [{ taskId: task.id, aid: f.aid }];
      for (const ref of refs) await api.patch(`/design/tasks/${ref.taskId}/files/${ref.aid}`, { status });
      toast.success(zh ? "状态已更新" : "Updated");
    } catch (e) { toast.error(e?.response?.data?.detail || "failed"); }
    finally { onChanged && (await onChanged()); }
  };

  const toggleSelect = (aid) => setSelected((s) => (s.includes(aid) ? s.filter((x) => x !== aid) : s.length >= 2 ? [s[1], aid] : [...s, aid]));
  /* 传错版本可以删（2026-10-21 用户需求，恢复 2026-10-18 被整体下线的入口）。
     背景：10-18 曾按「附件只增不删、数据库一律留档」的定调把删除按钮和右键菜单项
     全部移除，前端只剩「传错了再传一版」。实际使用中用户传错文件后无法撤下错误版本，
     页面上还残留着「右键：…删除」的过期提示，只能找后端手工处理。
     本次口径调整为「可以删，但只删软删、且锁定版收紧」：
       · 软删除：后端 DELETE 把记录挪进 deletedFiles（含删除人 / 时间），版本号不重排，
         审计与历史追溯能力不变 —— 仍然满足「数据库留档」这条红线；
       · Draft 版本：有编辑权限的人都能删（多数是自己刚传错的草稿）；
       · Submitted / Approved 锁定版本：只有设计主管可删（与后端 400 的判定同口径，
         前端先藏起来，避免点下去才被拦）。
     删除后同时把它从「对比选择」里摘掉，否则已选清单会指向不存在的版本。 */
  const canDelete = (f) => canEdit && (isMgr || !f.locked);
  const del = async (f) => {
    if (!window.confirm(zh ? `删除 ${f.type} v${f.version}「${f.name}」？\n（软删除：记录移入历史留档可审计，版本号不重排）` : `Delete ${f.type} v${f.version}?`)) return;
    setDeleting(f.aid);
    try {
      const refs = pairedTasks ? f.pairRefs || [] : [{ taskId: task.id, aid: f.aid }];
      for (const ref of refs) await api.delete(`/design/tasks/${ref.taskId}/files/${ref.aid}`);
      setSelected((s) => s.filter((x) => x !== f.aid));
      toast.success(zh ? "已删除该版本（记录仍留档）" : "Deleted (record retained)");
    } catch (e) { toast.error(e?.response?.data?.detail || (zh ? "删除失败" : "Delete failed")); }
    finally { setDeleting(null); onChanged && (await onChanged()); }
  };
  const uploadNewVersion = (f) => { setFtype(f.type); setTimeout(() => inputRef.current?.click(), 0); };
  const menuFor = (f) => () => [
    f.fileId && { key: "preview", icon: Eye, label: zh ? "预览" : "Preview", onClick: () => setPreview(lbItems.findIndex((x) => x.id === f.aid)) },
    f.fileId && { key: "download", icon: Download, label: zh ? "下载" : "Download", onClick: () => downloadFile(f.fileId, f.name).catch((e) => toast.error(e?.message || (zh ? "下载失败" : "Download failed"))) },
    { key: "compare", icon: selected.includes(f.aid) ? CheckSquare : Square, label: selected.includes(f.aid) ? (zh ? "移出对比" : "Remove from compare") : (zh ? "加入对比" : "Add to compare"), hint: `${selected.length}/2`, onClick: () => toggleSelect(f.aid) },
    canEdit && { key: "new-version", icon: UploadCloud, label: zh ? `上传新版本（${f.type}）` : `Upload new version (${f.type})`, onClick: () => uploadNewVersion(f) },
    canDelete(f) && { sep: true },
    canDelete(f) && { key: "delete", icon: Trash2, label: zh ? `删除 v${f.version}（软删除留档）` : `Delete v${f.version}`, danger: true, onClick: () => del(f) },
  ];

  const selectedFiles = selected.map((aid) => files.find((f) => f.aid === aid)).filter(Boolean);
  const sorted = files.slice().sort((a, b) => (a.type === b.type ? b.version - a.version : (a.type || "").localeCompare(b.type || "")));
  const lbItems = sorted.filter((f) => f.fileId).map((f) => ({ id: f.aid, fileId: f.fileId, size: f.size, url: fileUrl(f.fileId), kind: mediaKind(f.name, f.contentType), name: `${f.type} v${f.version} · ${f.name}`, meta: `${fmtSize(f.size)} · ${f.uploader} · ${uploadDateLabel(f.uploadedAt, f.at)} · ${f.status}` }));

  const Viewer = ({ f, small }) => {
    if (!f) return <div className="flex items-center justify-center h-full text-slate-400 text-sm">—</div>;
    // 对比视图同样改走逐页渲染，避免两个大 PDF iframe 同时加载导致浏览器黑屏。
    if (f.ext === "pdf" && f.fileId) return <div className="w-full h-full min-h-0" data-testid={`design-file-preview-pages-${f.aid}`}><ProjectPdfPreview api={api} file={f} zh={zh} /></div>;
    // Office（docx/xlsx/pptx…）：后端转 PDF 后逐页渲染，页内就能读完，
    // 不用先下载再用本地软件打开（2026-10-20 用户需求）。
    if (OFFICE_EXT.includes((f.ext || "").toLowerCase()) && f.fileId) return <div className="w-full h-full min-h-0" data-testid={`design-file-preview-office-${f.aid}`}><ProjectPdfPreview api={api} file={f} zh={zh} convert /></div>;
    if (f.previewable && f.fileId) return <img alt={f.name} src={fileUrl(f.fileId)} className="w-full h-full object-contain rounded-lg border border-slate-200 bg-slate-50" data-testid={`design-file-preview-img-${f.aid}`} />;
    return (
      <div className="flex flex-col items-center justify-center h-full text-slate-400 text-sm gap-2 border border-dashed border-slate-200 rounded-lg">
        <Box className="h-8 w-8" />
        <p>{zh ? "该格式无法在浏览器预览（如 CAD / CDR）" : "Not previewable in browser"}</p>
        {/* CAD / CDR 等无法页内预览的格式：原来用 target=_blank 打开下载接口，
            浏览器会留下空白标签页；改为 blob 直接落盘后再提示。 */}
        {f.fileId && <button type="button" onClick={() => downloadFile(f.fileId, f.name).catch((err) => toast.error(err?.message || (zh ? "下载失败" : "Download failed")))}
          data-testid={`design-file-preview-download-${f.aid}`} className="text-indigo-600 underline">{zh ? "下载查看" : "Download"}</button>}
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4" onClick={onClose} data-testid="design-files-modal">
      <div className="bg-white w-full max-w-4xl overflow-hidden flex flex-col rounded-2xl max-h-[90dvh]" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100 flex-wrap md:flex-nowrap">
          <div className="min-w-0">
            <p className="text-sm font-bold text-slate-800 truncate">{zh ? "文件与版本" : "Files & Versions"} · {T(task.title)}</p>
            <p className="text-[11px] text-slate-400">{pairedTasks
              ? (zh ? "一份 PDF 同步登记到 Planning 和 Building；CAD 登记到规划图纸。历史版本合并显示。" : "One PDF is recorded for Planning and Building; CAD goes to Planning. Versions are shown together.")
              : (zh ? "拖拽上传 PDF / 图片 / CAD（自动版本管理，Submitted/Approved 锁定）· 行上右键：预览 · 下载 · 对比 · 新版本 · 删除" : "Drag-drop PDF/Image/CAD, auto-versioned · right-click a row for actions")}</p>
          </div>
          <button onClick={onClose} data-testid="design-files-close" className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 text-slate-400 hover:text-slate-700"><X className="h-5 w-5" /></button>
        </div>

        <div className="p-5 overflow-y-auto space-y-4">
          {/* uploader */}
          {canEdit && !pairedTasks && <div className="flex flex-wrap items-center gap-2">
            <div className="flex-wrap md:flex-nowrap flex items-center gap-1">
              <label className="text-[11px] text-slate-500">{zh ? "类型" : "Type"}</label>
              <select value={ftype} onChange={(e) => setFtype(e.target.value)} data-testid="design-upload-type" className="text-[12px] rounded border border-slate-200 px-2 py-1 bg-white">
                {/* 2026-10-19 用户反馈：类型下拉原来直接显示英文键（如 811 Ticket），
                    看不出是什么。zh 模式统一显示中文名，并在下方补一句该类型的用途。 */}
                {typeOptions.map((t) => <option key={t} value={t} title={FILE_TYPE_HINT[t] ? T(FILE_TYPE_HINT[t]) : ""}>{TE(t, FILE_TYPE_ZH)}</option>)}
              </select>
            </div>
            <div className="flex-wrap md:flex-nowrap flex items-center gap-1">
              <label className="text-[11px] text-slate-500">{zh ? "状态" : "Status"}</label>
              <select value={fstatus} onChange={(e) => setFstatus(e.target.value)} data-testid="design-upload-status" className="text-[12px] rounded border border-slate-200 px-2 py-1 bg-white">
                {["Draft", "Submitted", "Approved"].map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            {FILE_TYPE_HINT[ftype] && <p className="w-full text-[10px] leading-4 text-slate-400" data-testid="design-upload-type-hint">{T(FILE_TYPE_HINT[ftype])}</p>}
          </div>}
          {canEdit && <div
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => { e.preventDefault(); setDragOver(false); doUpload(e.dataTransfer.files); }}
            onClick={() => inputRef.current?.click()}
            data-testid="design-upload-dropzone"
            className={`rounded-lg border-2 border-dashed p-6 text-center cursor-pointer transition-colors ${dragOver ? "border-indigo-500 bg-indigo-50" : "border-slate-200 hover:border-indigo-300 bg-slate-50"}`}
          >
            <UploadCloud className={`h-7 w-7 mx-auto mb-1 ${dragOver ? "text-indigo-600" : "text-slate-400"}`} />
            <p className="text-sm text-slate-600">{uploading ? (zh ? "上传中…" : "Uploading…") : (zh ? "拖拽文件到此处，或点击选择" : "Drop files or click")}</p>
            <p className="text-[10px] text-slate-400 mt-0.5">{pairedTasks
              ? (zh ? "PDF / DWG / DXF ≤ 200MB，按格式自动归类" : "PDF / DWG / DXF ≤ 200MB, categorized automatically")
              : (zh ? "PDF · 图片 · DWG/DXF · Office（含 PPT）· CDR · ZIP 等 ≤ 200MB（大文件自动分片）" : "PDF · images · CAD · Office (incl. PPT) · CDR · ZIP ≤ 200MB (chunked)")}</p>
            <input ref={inputRef} type="file" multiple className="hidden" onChange={(e) => doUpload(e.target.files)} data-testid="design-upload-input" accept={pairedTasks ? ".pdf,.dwg,.dxf" : DESIGN_UPLOAD_ACCEPT} />
          </div>}

          {/* compare toggle */}
          {files.length > 0 && (
            <div className="flex-wrap md:flex-nowrap flex items-center justify-between">
              <p className="text-xs font-semibold text-slate-600">{zh ? `版本记录（${files.length}）` : `Versions (${files.length})`}</p>
              <button
                disabled={selectedFiles.length !== 2}
                onClick={() => setCompare(true)}
                data-testid="design-files-compare-btn"
                className={"min-h-11 min-w-11 md:min-h-0 md:min-w-0 " + (`text-[11px] flex items-center gap-1 px-2.5 py-1 rounded-full ${selectedFiles.length === 2 ? "bg-indigo-600 text-white" : "bg-slate-100 text-slate-400"}`)}
              >
                <GitCompare className="h-3 w-3" />{zh ? `对比所选(${selectedFiles.length}/2)` : `Compare (${selectedFiles.length}/2)`}
              </button>
            </div>
          )}

          {/* version list */}
          <div className="space-y-1.5">
            {files.length === 0 && <p className="text-slate-400 text-sm text-center py-6">{zh ? "暂无文件" : "No files"}</p>}
            {sorted.map((f) => {
              const Icon = iconFor(f.ext);
              return (
                <div key={f.aid} onDoubleClick={() => f.fileId && setPreview(lbItems.findIndex((x) => x.id === f.aid))} title={f.fileId ? (zh ? "双击预览 · 右键更多操作" : "Double-click to preview · right-click for actions") : undefined} {...ctx.bind(menuFor(f))}
                  className={`flex items-center gap-2 rounded-lg border border-slate-100 bg-white px-3 py-2 select-none ${f.fileId ? "cursor-pointer hover:bg-indigo-50/40" : ""}`} data-testid={`design-file-row-${f.aid}`}>
                  <input type="checkbox" checked={selected.includes(f.aid)} onChange={() => toggleSelect(f.aid)} data-testid={`design-file-select-${f.aid}`} className="accent-indigo-600" />
                  <Icon className="h-4 w-4 text-slate-400 shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="text-xs text-slate-700 truncate">{TE(f.type, FILE_TYPE_ZH)} <span className="text-indigo-600 font-semibold">v{f.version}</span> {f.locked && <Lock className="h-3 w-3 inline text-amber-500" />}</p>
                    <p className="text-[10px] text-slate-400 truncate">{f.name} · {fmtSize(f.size)} · {f.uploader}{f.sha256 ? ` · #${f.sha256.slice(0, 8)}` : ""}</p>
                    <FileUploadDate value={f.uploadedAt} fallbackValue={f.at} className="block" />
                  </div>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full ${STATUS_COLORS[f.status] || STATUS_COLORS.Draft}`}>{f.status}</span>
                  {canEdit && (isMgr || !f.locked) && (
                    <select value={f.status} onChange={(e) => changeStatus(f, e.target.value)} data-testid={`design-file-status-${f.aid}`} className="text-[10px] rounded border border-slate-200 px-1 py-0.5 bg-white">
                      {["Draft", "Submitted", "Approved", "Superseded"].map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  )}
                  {/* 行尾操作：预览 / 下载 / 删除。删除按钮只在「有编辑权 + 非锁定版」时出现，
                      锁定版（Submitted/Approved）由后端要求设计主管，前端先按同一口径隐藏。
                      2026-10-21 恢复：10-18 曾整段移除，导致传错版本无法撤下。 */}
                  {f.fileId && <button onClick={() => setPreview(lbItems.findIndex((x) => x.id === f.aid))} title={zh ? "预览（可翻页 / 缩放）" : "Preview"} data-testid={`design-file-preview-${f.aid}`} className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 text-slate-400 hover:text-indigo-600"><Eye className="h-4 w-4" /></button>}
                  {f.fileId && (
                    <a href={fileUrl(f.fileId)} onClick={(e) => { e.preventDefault(); e.stopPropagation(); downloadFile(f.fileId, f.name).catch((err) => toast.error(err?.message || (zh ? "下载失败" : "Download failed"))); }}
                      target="_blank" rel="noreferrer" title="Download" data-testid={`design-file-download-${f.aid}`}
                      className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 inline-flex items-center justify-center text-slate-400 hover:text-indigo-600">
                      <Download className="h-4 w-4" />
                    </a>
                  )}
                  {canDelete(f) && (
                    <button onClick={() => del(f)} disabled={deleting === f.aid}
                      title={f.locked ? (zh ? "删除该版本（锁定版，仅设计主管可删；软删除留档）" : "Delete (manager only for locked versions)") : (zh ? "删除该版本（传错了用这个；软删除，记录仍留档）" : "Delete version (soft delete)")}
                      data-testid={`design-file-del-${f.aid}`}
                      className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 inline-flex items-center justify-center rounded text-slate-300 hover:text-red-600 disabled:opacity-40">
                      {deleting === f.aid ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                    </button>
                  )}
                </div>
              );
            })}
          </div>

          {preview !== null && lbItems[preview] && <MediaLightbox tid="design-file-lightbox" items={lbItems} index={preview} onIndex={setPreview} onClose={() => setPreview(null)} api={api} zh={zh} />}
          {ctx.element}
        </div>

        {/* compare overlay */}
        {compare && selectedFiles.length === 2 && (
          <div className="fixed inset-0 z-[70] bg-black/70 flex flex-col p-4" data-testid="design-file-compare-overlay">
            <div className="mobile-safe-topbar flex-wrap md:flex-nowrap flex items-center justify-between text-white mb-2">
              <p className="text-sm font-semibold">{zh ? "版本对比" : "Version Compare"}</p>
              <button onClick={() => setCompare(false)} data-testid="design-file-compare-close" className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 text-white/80 hover:text-white"><X className="h-5 w-5" /></button>
            </div>
            <div className="flex-1 grid grid-cols-2 gap-3 min-h-0">
              {selectedFiles.map((f, i) => (
                <div key={f.aid} className="flex flex-col min-h-0" data-testid={`design-compare-panel-${i}`}>
                  <p className="text-[11px] text-white/80 mb-1 truncate">{TE(f.type, FILE_TYPE_ZH)} <b>v{f.version}</b> · {f.status} · {f.name} · #{(f.sha256 || "").slice(0, 8)}</p>
                  <div className="flex-1 min-h-0 bg-white rounded-lg p-2"><Viewer f={f} small /></div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
